import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { LandingPage } from './pages/public/LandingPage';
import { PricingPage } from './pages/public/PricingPage';
import { ComparePage } from './pages/public/ComparePage';
import { NotFoundPage } from './pages/public/NotFoundPage';
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { MfaChallengePage } from './pages/auth/MfaChallengePage';
import { AppHome } from './pages/app/AppHome';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';
import { UploadPage } from './pages/app/UploadPage';
import { ImportMappingsPage } from './pages/app/ImportMappingsPage';
import { WorkspacePage } from './pages/app/WorkspacePage';
import { ForecastPage } from './pages/app/ForecastPage';
import { BoardPackPage } from './pages/app/BoardPackPage';
import { ApprovalsPage } from './pages/app/ApprovalsPage';
import { SettingsPage } from './pages/app/SettingsPage';
import { SubscriptionPage } from './pages/app/SubscriptionPage';
import { PLPage } from './pages/app/PLPage';
import { PivotPage } from './pages/app/PivotPage';
import { OrgChangesPage } from './pages/app/OrgChangesPage';
import { KpiPage } from './pages/app/KpiPage';
import { SignalsPage } from './pages/app/SignalsPage';
import { AllocationPage } from './pages/app/AllocationPage';
import { CompilationPage } from './pages/app/CompilationPage';
import { CompilationDetailPage } from './pages/app/CompilationDetailPage';
import { AuditLogPage } from './pages/app/AuditLogPage';
import { QaPage } from './pages/app/QaPage';
import { ConnectorsPage } from './pages/app/ConnectorsPage';
import { SsoAdminPage } from './pages/app/SsoAdminPage';
import { ScimAdminPage } from './pages/app/ScimAdminPage';
import { AdminPage } from './pages/app/AdminPage';
import { WorkbooksPage } from './pages/app/WorkbooksPage';
import { EntitiesPage } from './pages/app/EntitiesPage';
import { FxRatesPage } from './pages/app/FxRatesPage';
import { WorkforcePage } from './pages/app/WorkforcePage';
import { ProfilePage } from './pages/app/ProfilePage';
import { AiResultsPage } from './pages/app/AiResultsPage';
import { PlanningPage } from './pages/app/PlanningPage';
import { AccountSearchPage } from './pages/app/AccountSearchPage';
import { DemoPage } from './pages/public/DemoPage';
import { SharedSnapshotPage } from './pages/public/SharedSnapshotPage';
import { PrivacyPage } from './pages/public/PrivacyPage';
import { TermsPage } from './pages/public/TermsPage';
import { DpaPage } from './pages/public/DpaPage';
import { SecurityPage } from './pages/public/SecurityPage';

// Univer is ~1.3 MB gzipped; we lazy-load the editor so it never lands
// in the main bundle for users who don't open a workbook.
const WorkbookEditorPage = lazy(() => import('./pages/app/WorkbookEditorPage'));

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
      …
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/share/:id" element={<SharedSnapshotPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/app" element={<AppHome />} />
      <Route path="/app/profile" element={<ProfilePage />} />
      <Route path="/app/workspace/:wsId" element={<WorkspacePage />} />
      <Route path="/app/workspace/:wsId/upload" element={<UploadPage />} />
      <Route path="/app/workspace/:wsId/import-mappings" element={<ImportMappingsPage />} />
      <Route path="/app/workspace/:wsId/forecast" element={<ForecastPage />} />
      <Route path="/app/workspace/:wsId/pl" element={<PLPage />} />
      <Route path="/app/workspace/:wsId/pivot" element={<PivotPage />} />
      <Route path="/app/workspace/:wsId/kpis" element={<KpiPage />} />
      <Route path="/app/workspace/:wsId/signals" element={<SignalsPage />} />
      <Route path="/app/workspace/:wsId/org-changes" element={<OrgChangesPage />} />
      <Route path="/app/workspace/:wsId/allocations" element={<AllocationPage />} />
      <Route path="/app/workspace/:wsId/compilation" element={<CompilationPage />} />
      <Route path="/app/workspace/:wsId/compilation/:projectId" element={<CompilationDetailPage />} />
      <Route path="/app/workspace/:wsId/board-pack" element={<BoardPackPage />} />
      <Route path="/app/workspace/:wsId/approvals" element={<ApprovalsPage />} />
      <Route path="/app/workspace/:wsId/settings" element={<SettingsPage />} />
      <Route path="/app/workspace/:wsId/subscription" element={<SubscriptionPage />} />
      <Route path="/app/workspace/:wsId/audit-log" element={<AuditLogPage />} />
      <Route path="/app/workspace/:wsId/qa" element={<QaPage />} />
      <Route path="/app/workspace/:wsId/ai-results" element={<AiResultsPage />} />
      <Route path="/app/workspace/:wsId/planning" element={<PlanningPage />} />
      <Route path="/app/workspace/:wsId/account-search" element={<AccountSearchPage />} />
      <Route path="/app/workspace/:wsId/connectors" element={<ConnectorsPage />} />
      <Route path="/app/workspace/:wsId/sso" element={<SsoAdminPage />} />
      <Route path="/app/workspace/:wsId/scim" element={<ScimAdminPage />} />
      <Route path="/app/workspace/:wsId/workbooks" element={<WorkbooksPage />} />
      <Route path="/app/workspace/:wsId/entities" element={<EntitiesPage />} />
      <Route path="/app/workspace/:wsId/fx-rates" element={<FxRatesPage />} />
      <Route path="/app/workspace/:wsId/workforce" element={<WorkforcePage />} />
      <Route
        path="/app/workspace/:wsId/workbook/:workbookId"
        element={
          <Suspense fallback={<LazyFallback />}>
            <WorkbookEditorPage />
          </Suspense>
        }
      />
      <Route path="/app/admin" element={<AdminPage />} />

      {/* Auth surface — credential + OAuth, editorial split-screen */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/register" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/mfa-challenge" element={<MfaChallengePage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
